import {
  Box,
  Slider,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { RangeConfig } from "src/config/filterConfig";

interface RangeFilterProps {
  /** Current range values */
  value: { min?: number; max?: number } | undefined;

  /** Callback when range changes */
  onChange: (range: { min?: number; max?: number } | undefined) => void;

  /** Range configuration */
  config?: RangeConfig;

  /** Whether to show text inputs */
  showInputs?: boolean;

  /** Debounce delay for input changes (ms) */
  debounceMs?: number;
}

/**
 * Numeric range filter with slider and optional text inputs
 *
 * Features:
 * - Dual-thumb slider for range selection
 * - Optional min/max text inputs
 * - Unit display (e.g., "$", "MB")
 * - Debounced updates
 * - Clear when both inputs empty
 */
const RangeFilter: React.FC<RangeFilterProps> = ({
  value,
  onChange,
  config = {},
  showInputs = true,
  debounceMs = 500,
}) => {
  const theme = useTheme();
  const {
    min: configMin = 0,
    max: configMax = 100,
    step = 1,
    unit = "",
    unitPosition = "before",
    formatValue,
  } = config;

  // Local state for smooth slider interaction
  const [localRange, setLocalRange] = useState<[number, number]>([
    value?.min ?? configMin,
    value?.max ?? configMax,
  ]);

  // Local state for text inputs
  const [minInput, setMinInput] = useState(value?.min?.toString() ?? "");
  const [maxInput, setMaxInput] = useState(value?.max?.toString() ?? "");

  // Sync local state with external value
  useEffect(() => {
    setLocalRange([value?.min ?? configMin, value?.max ?? configMax]);
    setMinInput(value?.min?.toString() ?? "");
    setMaxInput(value?.max?.toString() ?? "");
  }, [value, configMin, configMax]);

  // Debounced onChange
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout>();

  const debouncedOnChange = useCallback(
    (newRange: { min?: number; max?: number } | undefined) => {
      if (debounceTimer) clearTimeout(debounceTimer);

      const timer = setTimeout(() => {
        onChange(newRange);
      }, debounceMs);

      setDebounceTimer(timer);
    },
    [onChange, debounceMs],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    const [newMin, newMax] = newValue as [number, number];
    setLocalRange([newMin, newMax]);
    setMinInput(newMin.toString());
    setMaxInput(newMax.toString());
  };

  const handleSliderChangeCommitted = (
    _event: React.SyntheticEvent | Event,
    newValue: number | number[],
  ) => {
    const [newMin, newMax] = newValue as [number, number];

    // If range is at full extent, clear the filter
    if (newMin === configMin && newMax === configMax) {
      onChange(undefined);
    } else {
      onChange({
        min: newMin !== configMin ? newMin : undefined,
        max: newMax !== configMax ? newMax : undefined,
      });
    }
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setMinInput(inputValue);

    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(
        configMin,
        Math.min(numValue, localRange[1]),
      );
      setLocalRange([clampedValue, localRange[1]]);

      debouncedOnChange({
        min: clampedValue !== configMin ? clampedValue : undefined,
        max: value?.max,
      });
    } else if (inputValue === "") {
      setLocalRange([configMin, localRange[1]]);
      debouncedOnChange({
        min: undefined,
        max: value?.max,
      });
    }
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setMaxInput(inputValue);

    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      const clampedValue = Math.min(
        configMax,
        Math.max(numValue, localRange[0]),
      );
      setLocalRange([localRange[0], clampedValue]);

      debouncedOnChange({
        min: value?.min,
        max: clampedValue !== configMax ? clampedValue : undefined,
      });
    } else if (inputValue === "") {
      setLocalRange([localRange[0], configMax]);
      debouncedOnChange({
        min: value?.min,
        max: undefined,
      });
    }
  };

  const formatDisplayValue = (val: number): string => {
    if (formatValue) return formatValue(val);

    const formatted = val.toLocaleString();
    if (unit) {
      return unitPosition === "before"
        ? `${unit}${formatted}`
        : `${formatted} ${unit}`;
    }
    return formatted;
  };

  const hasActiveRange =
    (value?.min !== undefined && value.min !== configMin) ||
    (value?.max !== undefined && value.max !== configMax);

  return (
    <Box>
      {/* Slider */}
      <Box sx={{ px: 1, pt: 1 }}>
        <Slider
          value={localRange}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderChangeCommitted}
          min={configMin}
          max={configMax}
          step={step}
          valueLabelDisplay="auto"
          valueLabelFormat={formatDisplayValue}
          sx={{
            "& .MuiSlider-thumb": {
              width: 16,
              height: 16,
              "&:hover, &.Mui-focusVisible": {
                boxShadow: `0 0 0 8px ${alpha(
                  theme.palette.primary.main,
                  0.16,
                )}`,
              },
            },
            "& .MuiSlider-track": {
              height: 4,
            },
            "& .MuiSlider-rail": {
              height: 4,
              opacity: 0.3,
            },
            "& .MuiSlider-valueLabel": {
              fontSize: "0.75rem",
              padding: "2px 6px",
            },
          }}
        />
      </Box>

      {/* Range labels */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 0.5,
          mb: showInputs ? 1.5 : 0,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.7rem" }}
        >
          {formatDisplayValue(configMin)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.7rem" }}
        >
          {formatDisplayValue(configMax)}
        </Typography>
      </Box>

      {/* Text inputs */}
      {showInputs && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Min"
            value={minInput}
            onChange={handleMinInputChange}
            type="number"
            inputProps={{
              min: configMin,
              max: configMax,
              step,
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8125rem",
              },
              "& .MuiOutlinedInput-input": {
                py: 0.75,
                textAlign: "center",
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            –
          </Typography>
          <TextField
            size="small"
            placeholder="Max"
            value={maxInput}
            onChange={handleMaxInputChange}
            type="number"
            inputProps={{
              min: configMin,
              max: configMax,
              step,
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8125rem",
              },
              "& .MuiOutlinedInput-input": {
                py: 0.75,
                textAlign: "center",
              },
            }}
          />
        </Box>
      )}

      {/* Active range indicator */}
      {hasActiveRange && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1,
            color: theme.palette.primary.main,
            fontSize: "0.75rem",
            textAlign: "center",
          }}
        >
          {value?.min !== undefined && value?.max !== undefined
            ? `${formatDisplayValue(value.min)} – ${formatDisplayValue(
                value.max,
              )}`
            : value?.min !== undefined
              ? `${formatDisplayValue(value.min)} and above`
              : `Up to ${formatDisplayValue(value!.max!)}`}
        </Typography>
      )}
    </Box>
  );
};

export default RangeFilter;
